import { Carousel, Flex, Box, spaces } from '@electricjs/arc';

import { Store } from '@/types/storefront';
import { Product } from '@/types/products';
import { StorefrontAsset } from '@/types/assets';
import { useGlobalUI } from '@/components/GlobalUIProvider';

import { ItemCard } from './ItemCard';
import { ItemDetailsModal } from './ItemDetailsModal';

// fixing the card dimensions, so it can be used inside the carousel
const CARD_WIDTH = '180px';
const CARD_HEIGHT = '310px'; // minimum height to fit everything when language is french

type CatalogCarousel = {
  store: Store.Catalog;
  items?: Product[];
  onAddItem: (item: Product) => void;
  onRemoveItem: (item: Product) => void;
};

type AssetCarousel = {
  store: Store.Asset;
  items?: StorefrontAsset[];
  onAddItem: (item: StorefrontAsset) => void;
  onRemoveItem: (item: StorefrontAsset) => void;
};

type ItemCarouselProps = (CatalogCarousel | AssetCarousel) & {
  id: string;
  selectedItemsIds: string[];
  isEmployeeSelected?: boolean;
};

export const ItemCarousel = ({
  id,
  store,
  items,
  selectedItemsIds,
  onAddItem,
  onRemoveItem,
  isEmployeeSelected,
}: ItemCarouselProps) => {
  const { openModal, closeModal } = useGlobalUI();

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Flex id={id} vertical width="100%">
      <Carousel
        scrollAmountPerClick={360} // usually, twice the width of each item
        carouselRowProps={{
          padding: spaces.space_3xs, // the same value of the card border when selected
          gap: spaces.space_m,
        }}>
        {items.map(item => {
          if (store === Store.Catalog) {
            const catalogItem = item as Product;

            return (
              <Box key={item.id} width={CARD_WIDTH} height={CARD_HEIGHT}>
                <ItemCard
                  idPrefix={id}
                  store={store}
                  item={catalogItem}
                  selected={selectedItemsIds.includes(item.id)}
                  onAddItem={() => onAddItem(catalogItem)}
                  onRemoveItem={() => onRemoveItem(catalogItem)}
                  isCondensed
                  onClick={() =>
                    openModal(
                      <ItemDetailsModal
                        store={store}
                        item={catalogItem}
                        onAddItem={() => onAddItem(catalogItem)}
                        onRemoveItem={() => onRemoveItem(catalogItem)}
                        onClose={closeModal}
                        selected={selectedItemsIds.includes(item.id)}
                        isEmployeeSelected={isEmployeeSelected}
                      />
                    )
                  }
                />
              </Box>
            );
          } else if (store === Store.Asset) {
            const assetItem = item as StorefrontAsset;

            return (
              <Box key={item.id} width={CARD_WIDTH} height={CARD_HEIGHT}>
                <ItemCard
                  idPrefix="related-product-accessory"
                  store={store}
                  item={assetItem}
                  selected={selectedItemsIds.includes(item.id)}
                  onAddItem={() => onAddItem(assetItem)}
                  onRemoveItem={() => onRemoveItem(assetItem)}
                  isCondensed
                  onClick={() =>
                    openModal(
                      <ItemDetailsModal
                        store={store}
                        item={assetItem}
                        onAddItem={() => onAddItem(assetItem)}
                        onRemoveItem={() => onRemoveItem(assetItem)}
                        onClose={closeModal}
                        selected={selectedItemsIds.includes(item.id)}
                        isEmployeeSelected={isEmployeeSelected}
                      />
                    )
                  }
                />
              </Box>
            );
          }
        })}
      </Carousel>
    </Flex>
  );
};
